import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Logger, type AppState } from '@nextgen-web-framework/core';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import {
  NextgenImage,
  ButtonSize,
  ButtonStyles,
  ButtonWidth,
  useTranslation,
  InputValidations,
  useNextgenRouter,
  useBreakpoint,
  Breakpoints,
  LoaderType,
  AccountType,
} from '@nextgen-web-framework/all';
import {
  DynamicObjectInterface,
  RegistrationFlowDataType,
  RoutePaths,
  ImageReturnType,
  profileApi,
} from '@commerce-profile-webcomponents/sdk';
import { identityApi } from '@commerce-identity-webcomponents/sdk';
import { PageLoader } from '@commerce-webcomponents/ui';
import CoAppCommonScreen from '../CoAppCommonScreen/CoAppCommonScreen';
import PhoneNumberInput from '../../Common/PhoneNumberInput/PhoneNumberInput';
import DesktopLayout from '../../Common/DesktopLayout/DesktopLayout';
import ButtonWithLoading from '../../Common/ButtonWithLoading/ButtonWithLoading';
import { useCountryMasterData } from '../../../utils/masterdata';
import { parsePhoneNumber, removePhoneLeadingZero, parseNumber, parseString, iff } from '../../../utils/helper';
import { useLocale } from '../../../utils/customHooks';
import { CoAppScreenEnum } from '../types/propTypes';
import { RegistrationCommonProps, NextHandlerProps } from '../../Registration/types/propTypes';
import logObject from '../../../../logObject';
import { COAPP_STEPS } from '../../../utils/Constants';
import styles from './CoAppInvite.module.scss';

const CoAppInvite = ({ nextHandler }: RegistrationCommonProps) => {
  const logger = Logger.getInstance(logObject('CoAppInvite.tsx', 'CoAppInvite'));
  const t = useTranslation();
  const { push } = useNextgenRouter();
  const breakpoint = useBreakpoint();
  const isDesktop = ![Breakpoints.XS, Breakpoints.SM, Breakpoints.MD].includes(breakpoint as Breakpoints);

  const countryLabel = t('profile.coApp.inviteCountry');

  const { currentCountry } = useCountryMasterData();

  const registrationData = useSelector(
    (state: AppState) => state.commerceProfile.registrationFlowData,
  ) as RegistrationFlowDataType;
  const { userData, OtpData, flowName } = registrationData;
  // cms image
  const currentLocale = useLocale();
  const registrationCmsData = useSelector((state: AppState) => state.commerceProfile.registrationCmsData);
  const { coApp } = registrationCmsData;
  const accountTypeImagesCmsData = useSelector((state: AppState) => state.commerceProfile.accountTypeImagesCMS);
  const selectedAccountType: AccountType = useSelector((state: AppState) => state.commerceProfile.selectedAccountType);

  const idVerificationImage = selectedAccountType
    ? accountTypeImagesCmsData?.[selectedAccountType]?.idVerificationImage
    : undefined;
  const idStepDesktopImage = idVerificationImage?.find((item: ImageReturnType) => item?.viewport.includes(breakpoint));

  const [dialCode, setDialCode] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isUsernameVerified, setIsUsernameVerified] = useState(false);

  const handleInviteNextButton = async () => {
    setIsLoading(true);
    const localeStr = currentLocale ? `/${currentLocale}` : '';
    const payload: NextHandlerProps = {
      interimPhone: parsePhoneNumber(dialCode, phoneNumber),
      fullUrl: `${localeStr}/signup?registrationId=${userData.registrationId}&flowName=${flowName}&stepName=${COAPP_STEPS.P2_ID_STEP}&returnUrl=/login`,
      otpRequestId: OtpData.challengeId,
      version: parseNumber(userData.patchVersion),
    } as DynamicObjectInterface;
    const res = await Promise.resolve(nextHandler?.(payload, undefined, true, false));
    if (res?.registrationId) {
      setShowConfirmation(true);
    } else if (res?.responseBody) {
      const { code } = res.responseBody;
      setIsPhoneValid(false);
      setErrMsg(`profile.coApp.error.${code}`);
    }
    setIsLoading(false);
  };

  const handleInvitationBackButton = () => {
    push?.(RoutePaths.DASHBOARD);
  };

  const handlePhoneChange = (number?: string) => {
    setPhoneNumber(removePhoneLeadingZero(parseString(number)));
  };

  useEffect(() => {
    if (currentCountry?.phoneCode) {
      setDialCode(`(+${currentCountry?.phoneCode})`);
    }
  }, [currentCountry]);

  useEffect(() => {
    const phoneValid =
      (dialCode && phoneNumber && isValidPhoneNumber(parsePhoneNumber(dialCode, phoneNumber))) ||
      !dialCode ||
      !phoneNumber;
    setIsPhoneValid(phoneValid);
    if (dialCode && phoneNumber) {
      setErrMsg(iff(phoneValid, '', 'profile.validationMsg.phone.format'));
      const username = parsePhoneNumber(dialCode, phoneNumber);
      // validate username only if phone is valid
      handleUsernameCheck(username, phoneValid);
    } else {
      setErrMsg('');
    }
  }, [dialCode, phoneNumber]);

  const checkUserExist = async (username: string) => {
    try {
      const [profileResponse, identityResponse] = await Promise.all([
        profileApi.checkAccountUserExists({ username }),
        identityApi.checkUserExist({ username }),
      ]);

      logger.log(profileResponse?.profileExists, identityResponse, 'profileResponse');
      const isUnique = !profileResponse?.profileExists && !identityResponse?.isExist;

      return isUnique;
    } catch (error) {
      logger.error(error);
      return false;
    }
  };

  const handleUsernameCheck = async (username: string, phoneValid: boolean) => {
    if (phoneValid) {
      setIsLoading(true);

      const isUnique = await checkUserExist(username);
      if (!isUnique) {
        setIsPhoneValid(false);
        setErrMsg('profile.coApp.error.comregistrationas-account-exists');
        setIsUsernameVerified(false);
      } else {
        setErrMsg('');
        setIsUsernameVerified(true);
      }
      setIsLoading(false);
    } else {
      setIsUsernameVerified(false);
    }
  };

  const errorMessage = errMsg ? t(errMsg) : '';
  const isPhoneFilled = phoneNumber && dialCode;

  if (showConfirmation) {
    return (
      <CoAppCommonScreen buttonHandler={handleInvitationBackButton} screenType={CoAppScreenEnum.INVITATION_SENT} />
    );
  } else {
    return (
      <>
        <DesktopLayout desktopImage={idStepDesktopImage} containerClassName={styles['co-app-invite-layout']}>
          <div className={styles['co-app-invite-container']} data-testid="co-app-invite-container">
            <div className={styles['co-app-invite']}>
              <div className={styles['co-app-invite-form']} data-testid="co-app-invite-form">
                <div className={styles['co-app-invite-header']} data-testid="co-app-invite-header">
                  {t('profile.coApp.invitationTitle')}
                </div>
                {!isDesktop && (
                  <div className={styles['co-app-invite-image']} data-testid="co-app-invite-image">
                    <NextgenImage
                      src={coApp?.registrationImage?.url}
                      loaderType={LoaderType.CONTENTSTACK}
                      alt={coApp?.registrationImage?.altText}
                      fill={true}
                    />
                  </div>
                )}

                <div className={styles['co-app-invite-label']}>{t('profile.coApp.invitePhoneNumberLink')}</div>
                <div className={styles['co-app-invite-phone']} data-testid="co-app-invite-phone">
                  <PhoneNumberInput
                    name={'phone'}
                    data-testid="phone-testid"
                    dropdownLabel={countryLabel}
                    dropdownValue={dialCode}
                    disabledFilter
                    dropdownDisabled={!!currentCountry?.phoneCode}
                    configData={{
                      label: 'name',
                      onSelect: (e: DynamicObjectInterface) => {
                        setDialCode(e.name);
                      },
                    }}
                    inputLabel={t('profile.coApp.invitePhoneNumber')}
                    inputValue={phoneNumber}
                    onInputChange={handlePhoneChange}
                    required
                    validationStatus={iff(isPhoneValid, InputValidations.DEFAULT, InputValidations.INVALID)}
                    validationMessage={errorMessage}
                    {...(!isUsernameVerified && isPhoneFilled
                      ? {
                          status: InputValidations.INVALID,
                        }
                      : {})}
                  />
                </div>
              </div>
              <div className={styles['co-app-invite-next-btn']}>
                <ButtonWithLoading
                  title={t('profile.coApp.button.inviteSentLink')}
                  data-testid="next-btn-testid"
                  buttonStyle={ButtonStyles.SOLID}
                  size={ButtonSize.MEDIUM}
                  disabled={!isUsernameVerified || !dialCode || !phoneNumber}
                  onClick={handleInviteNextButton}
                  buttonWidth={ButtonWidth.FULL_WIDTH}
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </DesktopLayout>
        {isLoading && <PageLoader />}
      </>
    );
  }
};
export default CoAppInvite;
